import { collection, getDocs, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../../../../firebase/config';
import { createUserWithEmailAndPassword, updateCurrentUser } from 'firebase/auth';
import type { Company } from '../../../../companies/service/companiesService';

export type CompanyFormData = {
    companyName: string;
    companyType: string;
    companyId?: string;
    companyNo?: string;
    email?: string;
    companyAddress?: string;
    contactPersonName?: string;
    contactMobileNumber?: string;
    contactEmail?: string;
    selectedCompanyFromList?: Company | null;
    selectedCCTVCompanyFromList?: Company | null;
    selectedBAMSCompanyFromList?: Company | null;
    password?: string;
    secondaryEmails?: string[];
};

export async function fetchGlobalCompanies(companyType?: string): Promise<Company[]> {
    const companiesSnap = await getDocs(collection(db, 'companies'));
    let companiesList = companiesSnap.docs.map(d => ({
        id: d.id,
        ...d.data()
    } as Company));

    if (companyType === 'Guard Company') {
        companiesList = companiesList.filter(company => {
            const type = company.companyType;
            return !type || (type !== 'CCTV' && type !== 'BAMS');
        });
    } else if (companyType) {
        companiesList = companiesList.filter(company => company.companyType === companyType);
    }

    return companiesList;
}

export async function submitCompany(
    bankId: string,
    formData: CompanyFormData,
    uid: string
): Promise<void> {
    const emailToUse = (formData.email || formData.contactEmail || '').trim().toLowerCase();

    // ── Detect existing vendor early (before auth) ──
    const isExistingVendor =
        (formData.companyType === 'Guard Company' && !!formData.selectedCompanyFromList) ||
        (formData.companyType === 'CCTV' && !!formData.selectedCCTVCompanyFromList) ||
        (formData.companyType === 'BAMS' && !!formData.selectedBAMSCompanyFromList);

    // ── 1. Create Firebase Auth user ONLY for NEW vendors ──
    let authUid = '';
    if (!isExistingVendor && formData.password && formData.password.trim() && emailToUse) {
        const currentUser = auth.currentUser;
        if (currentUser) {
            const adminUser = currentUser;
            sessionStorage.setItem('creatingUser', currentUser.uid);
            try {
                const userCredential = await createUserWithEmailAndPassword(auth, emailToUse, formData.password.trim());
                authUid = userCredential.user.uid;
                try {
                    await adminUser.reload();
                    await updateCurrentUser(auth, adminUser);
                } catch {
                    // Silently keep admin session alive
                }
            } catch (error: any) {
                sessionStorage.removeItem('creatingUser');
                const code = error?.code;
                if (code === 'auth/email-already-in-use') {
                    throw new Error('This email is already registered in the authentication system.');
                } else if (code === 'auth/invalid-email') {
                    throw new Error('Invalid email address. Please check the email format.');
                } else if (code === 'auth/weak-password') {
                    throw new Error('Password is too weak. Please use a stronger password (at least 6 characters).');
                } else {
                    throw new Error(`Failed to create user account: ${error?.message || 'Unknown error'}`);
                }
            }
            sessionStorage.removeItem('creatingUser');
        }
    }

    // ── 3. Save / link company in Firestore ──
    if (isExistingVendor) {
        // ── EXISTING VENDOR: link this bank to the global /companies doc ──
        const selectedCompany =
            formData.selectedCompanyFromList ||
            formData.selectedCCTVCompanyFromList ||
            formData.selectedBAMSCompanyFromList!;

        const globalCompanyRef = doc(db, 'companies', selectedCompany.id);
        const globalCompanyDoc = await getDoc(globalCompanyRef);

        if (globalCompanyDoc.exists()) {
            const currentData = globalCompanyDoc.data();
            const bankClientArray: any[] = currentData.bankClient || [];
            const alreadyLinked = bankClientArray.some((e: any) => e.BankId === bankId);

            const updatePayload: any = {};
            if (!alreadyLinked) {
                updatePayload.bankClient = [...bankClientArray, { BankId: bankId }];
            }
            if (formData.contactPersonName)  updatePayload.contactPersonName  = formData.contactPersonName.trim();
            if (formData.contactMobileNumber) updatePayload.contactMobileNumber = formData.contactMobileNumber.trim();
            if (formData.contactEmail)        updatePayload.contactEmail        = formData.contactEmail.trim();
            if (authUid)                      updatePayload.authUid             = authUid;
            if (formData.secondaryEmails?.length) updatePayload.secondaryEmails = formData.secondaryEmails;

            if (Object.keys(updatePayload).length > 0) {
                await updateDoc(globalCompanyRef, updatePayload);
            }
        }

        // ── Also write a reference doc into banks/{bankId}/companies so it appears in the vendor list ──
        // fetchCompaniesData reads from this subcollection and enriches from the global doc via companyId.
        const bankCompanyRef = doc(db, 'banks', bankId, 'companies', selectedCompany.id);
        const bankCompanySnap = await getDoc(bankCompanyRef);

        const bankRefPayload: any = {
            companyId:   selectedCompany.id,
            companyType: formData.companyType,
            status:      'Active',
            linkedAt:    new Date(),
        };
        // Carry over any bank-specific contact overrides
        if (formData.contactPersonName)   bankRefPayload.contactPersonName   = formData.contactPersonName.trim();
        if (formData.contactMobileNumber) bankRefPayload.contactMobileNumber  = formData.contactMobileNumber.trim();
        if (formData.contactEmail)        bankRefPayload.contactEmail         = formData.contactEmail.trim();

        if (!bankCompanySnap.exists()) {
            // New link — create the reference doc
            await setDoc(bankCompanyRef, bankRefPayload);
        } else {
            // Already linked (re-add scenario) — just refresh the type / contact fields
            await updateDoc(bankCompanyRef, bankRefPayload);
        }
    } else {
        // ── NEW VENDOR: write to root /companies/{authUid or auto-id} ──
        const docId = authUid || doc(collection(db, 'companies')).id;

        const companySaveData: any = {
            companyName:    formData.companyName.trim(),
            companyType:    formData.companyType,
            companyId:      formData.companyId?.trim() || docId,
            companyNo:      formData.companyNo?.trim() || '',
            email:          emailToUse,
            companyAddress: formData.companyAddress?.trim() || '',
            userId:         docId,
            // Link this company to the bank immediately
            bankClient:     [{ BankId: bankId }],
            createdAt:      new Date(),
            status:         'Active',
        };

        // Optional fields
        if (formData.password)            companySaveData.password           = formData.password.trim();
        if (formData.contactPersonName)   companySaveData.contactPersonName  = formData.contactPersonName.trim();
        if (formData.contactMobileNumber) companySaveData.contactMobileNumber = formData.contactMobileNumber.trim();
        if (formData.contactEmail)        companySaveData.contactEmail        = formData.contactEmail.trim();
        if (authUid)                      companySaveData.authUid             = authUid;
        if (formData.secondaryEmails?.length) companySaveData.secondaryEmails = formData.secondaryEmails;

        await setDoc(doc(db, 'companies', docId), companySaveData);

        // ── Also write a reference doc into banks/{bankId}/companies so it appears in the vendor list ──
        const bankCompanyRef = doc(db, 'banks', bankId, 'companies', docId);
        const bankRefPayload: any = {
            companyId:   docId,
            companyType: formData.companyType,
            status:      'Active',
            linkedAt:    new Date(),
        };
        // Carry over any bank-specific contact details
        if (formData.contactPersonName)   bankRefPayload.contactPersonName   = formData.contactPersonName.trim();
        if (formData.contactMobileNumber) bankRefPayload.contactMobileNumber  = formData.contactMobileNumber.trim();
        if (formData.contactEmail)        bankRefPayload.contactEmail         = formData.contactEmail.trim();

        await setDoc(bankCompanyRef, bankRefPayload);
    }
}
